import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const EFTPS = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Electronic Funds Withdrawal (EFW) for IRS Tax Payments"
          description="Pay your federal taxes securely and easily with IRS Electronic Funds Withdrawal (EFW) when e-filing. No fees, no hassle, direct bank payments."
          keywords=""
          canonical="resources/electronic-funds-withdrawal-efw/"
          ogType="article"
          // jsonLdPricing="11.99"
          // jsonLdAggregateRatingValue="4.95"
          // jsonLdAggregateRatingCount="4278"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Electronic Fund Withdrawal{' '}
          <span className="highlight-container">
            <span className="highlight">(EFW) </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Pay Your Taxes in an Easy Way!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/electronic-funds-withdrawal-efw/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-10T09:00:00-05:00"
                      >
                        August 10, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-10T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        August 10, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Electronic Funds Withdrawal (EFW) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        Electronic Funds Withdrawal (EFW) is an IRS payment
                        option that allows taxpayers to pay eligible federal
                        taxes directly from a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          U.S. checking or savings account{' '}
                        </strong>{' '}
                        when <br /> e-filing a tax return or form.
                      </span>
                    </li>

                    <li>
                      <span>
                        Individuals, businesses, tax professionals, and other
                        taxpayers can use EFW for making eligible federal tax
                        payments through electronic filing.
                      </span>
                    </li>

                    <li>
                      <span>
                        EFW supports{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          one-time direct debit payments
                        </strong>
                        .It does not support recurring or partial payments.
                      </span>
                    </li>

                    <li>
                      <span>
                        EFW can be used for various eligible federal tax
                        payments, including individual income taxes, business
                        taxes, excise taxes, estimated taxes, and extension
                        payments.
                      </span>
                    </li>

                    <li>
                      <span>
                        The authorized payment is withdrawn electronically from
                        the specified U.S. bank account and transferred to the
                        U.S. Treasury.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Electronic Funds Withdrawal (EFW) is a convenient and secure
                  IRS payment option that allows taxpayers to pay their federal
                  taxes directly from their bank account when e-filing a return.
                  Whether you&apos;re an individual, business, or tax
                  professional, EFW simplifies the payment process and
                  eliminates the need for separate steps to make payments after
                  filing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  In this guide, you&apos;ll learn everything you need to know
                  about Electronic Funds Withdrawal (EFW)—how it works, who can
                  use it, the types of tax payments it supports, and how to
                  avoid common issues to ensure a smooth and secure tax payment
                  experience.
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is an Electronic Funds Withdrawal?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who can use Electronic Fund Withdrawal?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#eligible" sx={{ color: '#0202ff' }}>
                          Federal Tax payments eligible for EFW
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#forms" sx={{ color: '#0202ff' }}>
                          IRS Forms supported for EFW payments
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How Electronic Fund Withdrawal (EFW) Works?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When to use Electronic Fund Withdrawal?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howfew" sx={{ color: '#0202ff' }}>
                          How EFW payments are Processed?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common EFW Payment Issues and How to Resolve Them?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#features" sx={{ color: '#0202ff' }}>
                          Features and benefits of Electronic Fund Withdrawal
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#info" sx={{ color: '#0202ff' }}>
                          Important information using EFW
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is an Electronic Fund Withdrawal?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Electronic Funds Withdrawal (EFW) is an IRS-authorized payment
                  method that enables you to pay your tax due by authorizing a
                  direct debit from your checking or savings account. It’s
                  available only when you e-file your tax return or certain tax
                  forms using approved software or through a tax professional.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Ready to File and Pay Your Taxes in an Easy Way?
                      </Typography>
                      <Typography variant="body2">
                        Use Electronic Funds Withdrawal (EFW) to file and pay in
                        one simple, secure step.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': { color: '#fff' },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who can use Electronic Fund Withdrawal?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  EFW is available to:
                </Typography>

                <Typography component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Individual taxpayers</b> filing Forms 1040,{' '}
                      <Link
                        href="/resources/tax-extension/form-4868-instructions/"
                        sx={{ color: '#2020ff' }}
                      >
                        4868
                      </Link>
                      , etc.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Businesses</b> filing Forms{' '}
                      <Link
                        href="/resources/employment-tax/form-941-instructions/"
                        sx={{ color: '#2020ff' }}
                      >
                        941
                      </Link>
                      ,{' '}
                      <Link
                        href="/resources/excise-tax/form-2290-instructions/"
                        sx={{ color: '#2020ff' }}
                      >
                        2290
                      </Link>
                      , 1120, and others
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Tax professionals</b> submitting returns on behalf of
                      clients
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b> Taxpayers</b> requesting extensions or paying
                      estimated taxes{' '}
                    </li>
                  </ul>
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> EFW is not available when filing a paper return.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="eligible"
                >
                  Federal Tax payments eligible for EFW
                </Typography>

                <Typography variant="body1">
                  Electronic Funds Withdrawal (EFW) supports payment of a
                  variety of federal taxes, making it a convenient option for
                  both individuals and businesses. When you e-file your tax
                  return or related forms, you can authorize the IRS to withdraw
                  the payment directly from your U.S. bank account. Some of the
                  key types of federal taxes you can pay through EFW include:
                </Typography>

                <Typography component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Individual Income Taxes:</b> Pay personal taxes and any
                      balance due with individual forms.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Estimated Tax Payments:</b> Quarterly payments for
                      individuals and businesses.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Business Taxes</b> Includes payroll forms, corporate
                      tax forms, and other tax forms.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Excise Taxes:</b> Payments for heavy vehicle use and
                      excise tax claims.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Extension Payments:</b> Pay to extend filing deadlines
                      with individual forms business forms.
                    </li>
                  </ul>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="forms"
                >
                  IRS Forms supported for EFW payments
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Electronic Funds Withdrawal (EFW) is an IRS-approved payment
                  method that allows taxpayers to pay their federal taxes
                  directly from a bank account when filing electronically. This
                  option is available for a wide range of IRS forms, including
                  both individual and business returns, extensions, and excise
                  tax filings.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Electronic Funds Withdrawal for Individuals
                </Typography>

                <Box sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 1 }}
                  >
                    <Table aria-label="EFW Individual">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: '50%',
                            }}
                          >
                            Tax Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: '50%',
                            }}
                          >
                            Tax Year
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Form 1040 series</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>Current Year (2025)</li>
                              <li> Prior Year (2023-2024) </li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 1040-ES estimated tax</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>
                                {' '}
                                Current Year 2026 (Quarters due April 15, 2026,
                                June 15, 2026, September 15, 2026 and January
                                15, 2027)
                              </li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Form 2350</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>Current Year (2025) </li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 4868</TableCell>
                          <TableCell>
                            {' '}
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li> Current Year (2025) </li>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Electronic Funds Withdrawal for Businesses
                </Typography>

                <Box sx={{ mb: 5 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 1 }}
                  >
                    <Table aria-label="EFW Business">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: '50%',
                            }}
                          >
                            Tax Type
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: '50%',
                            }}
                          >
                            Tax Year
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>Form 706</TableCell>
                          <TableCell>
                            2025 and 2026 (January - December)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 709</TableCell>
                          <TableCell>
                            2025 and 2026 (January - December)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Form 720</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>
                                2024-2025 (March, June, September, December)
                              </li>
                              <li>2026 (March, June, September)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 940</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>Current year (2025)</li>
                              <li>Prior year (2023–2024)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Form 941/941</TableCell>
                          <TableCell>
                            2023-2026 (March, June, September, December)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 943</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>Current year (2025)</li>
                              <li>Prior year (2023-2024)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Form 944</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>Current year (2025)</li>
                              <li>Prior year (2023-2024)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 945</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>Current year (2025)</li>
                              <li>Prior year (2023-2024)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            Form 990-PF balance due and amended
                          </TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>2023-2025 (January–December)</li>
                              <li>2026 (January–November)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 990-PF estimated taxes</TableCell>
                          <TableCell>2026-2027 (January–December)</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            Form 990-T balance due and amended
                          </TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>2023 (December)</li>
                              <li>2024 and 2025 (January–December)</li>
                              <li>2026 (January – November)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 990-T estimated taxes</TableCell>
                          <TableCell>2026-2027 (January–December)</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            Form 1041 balance due and amended
                          </TableCell>
                          <TableCell>2023-2026 (January–December)</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 1041-ES estimated taxes</TableCell>
                          <TableCell>2026-2027 (January–December)</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            Form 1042 balance due and amended
                          </TableCell>
                          <TableCell>2025-2026 (December)</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 1065</TableCell>
                          <TableCell>2023-2026 (January–December)</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            Form 1120 series balance due and amended returns
                          </TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>2023-2025 (January–December)</li>
                              <li>2026 (January–November)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Form 1120 series estimated taxes
                          </TableCell>
                          <TableCell>2026-2027 (January–December)</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Form 2290</TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>2023 (July–December)</li>
                              <li>2024-2026 (January–December)</li>
                              <li>2027 (January–June)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Form 5330 balance due and amended
                          </TableCell>
                          <TableCell>
                            <Box
                              component="ul"
                              sx={{ pl: 2, mb: 0, '& li': { mb: 1 } }}
                            >
                              <li>2023 (December)</li>
                              <li>2024-2025 (January–December)</li>
                              <li>2026 (January–November)</li>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Form 7004 extension forms</TableCell>
                          <TableCell>2025-2027 (January–December)</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Form 8868 extension forms</TableCell>
                          <TableCell>2025-2027 (January–December)</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box
                    component="ul"
                    sx={{ mt: 2, mb: 3, '& li': { mb: 1.5 } }}
                  >
                    <li>
                      {' '}
                      For general information about Electronic Funds Withdrawal
                      (EFW), visit the{' '}
                      <Link
                        href="/resources/pay-taxes-by-efw/"
                        sx={{ color: '#2020ff' }}
                      >
                        Pay Taxes by Electronic Funds Withdrawal
                      </Link>{' '}
                      page.{' '}
                    </li>
                    <li>
                      {' '}
                      To explore other electronic payment methods, visit the{' '}
                      <Link
                        href="https://www.irs.gov/payments"
                        target="_blank"
                        sx={{ color: '#2020ff' }}
                      >
                        Electronic Payment Options
                      </Link>{' '}
                      homepage.{' '}
                    </li>
                  </Box>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="how"
                >
                  How Electronic Fund Withdrawal (EFW) works?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  There’s no separate enrollment process. Here’s how it works:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ol>
                    <li style={{ marginBottom: 16 }}>
                      Choose <b>EFW</b> as your payment method while e-filing.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Enter your bank account and routing numbers.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Authorize the IRS to withdraw the payment.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Submit the return. The IRS will process your payment along
                      with your form.
                    </li>
                  </ol>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="when"
                >
                  When to use Electronic Fund Withdrawal?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use EFW if you:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ol>
                    <li style={{ marginBottom: 16 }}>
                      Filing your federal tax return or extension{' '}
                      <b>electronically.</b>
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Want to <b>pay your taxes in one step</b> while e-filing.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Don’t want to enroll in a separate payment system like{' '}
                      <Link href="/resources/eftps/" sx={{ color: '#2020ff' }}>
                        EFTPS.
                      </Link>{' '}
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Prefer a <b>secure, direct debit</b> from your checking or
                      savings account.
                    </li>
                  </ol>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="howfew"
                >
                  How EFW payments are Processed?
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 2 } }}>
                    <li>
                      When you authorize an EFW payment, the{' '}
                      <b>U.S. Department of the Treasury</b>, through a Treasury
                      Financial Agent, is permitted to transfer the{' '}
                      <b>full payment amount</b> from your bank account to the
                      Treasury’s account.
                    </li>
                    <li>
                      Your bank statement will show a transaction labeled{' '}
                      <b>
                        &quot;IRS USA Tax Payment,&quot; &quot;IRS USA Tax
                        Pymt,&quot;
                      </b>{' '}
                      or a similar description as confirmation of your tax
                      payment.
                    </li>
                    <li>
                      If your requested payment date falls on a{' '}
                      <b>weekend or federal holiday</b>, the payment will be
                      processed on the next business day. Your bank may place a
                      hold on the funds and mark it as a pending transaction.
                    </li>
                    <li>
                      The entire payment amount is{' '}
                      <b>debited as a single transaction</b>. EFW does not
                      support recurring or partial payments.
                    </li>
                    <li>
                      <b>Federal Tax Deposits</b> (such as employment tax
                      deposits) <b>cannot</b> be made using EFW. For those,
                      refer to the instructions on the specific IRS form to find
                      acceptable payment methods.
                    </li>
                  </Box>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="common"
                >
                  Common EFW Payment Issues and How to Resolve Them?
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <Box
                    component="ul"
                    sx={{ pl: 3, mb: 3, '& li': { mb: 2, lineHeight: 1.7 } }}
                  >
                    <li>
                      <b>Incorrect Bank Information:</b> Double-check routing
                      and account numbers before submitting.
                    </li>
                    <li>
                      <b>Payment Rejected:</b> If your payment fails due to
                      insufficient funds or other issues, you’ll receive IRS
                      Letter 4870 with instructions.
                    </li>
                    <li>
                      <b>Cancelled Payments:</b> You can cancel an EFW payment
                      by calling IRS e-file Payment Services at{' '}
                      <b>888-353-4537</b>, but only before the payment date and
                      within IRS guidelines.
                    </li>
                    <li>
                      <b>Early Bank Release:</b> If your bank processes the
                      payment earlier than expected, contact your bank for
                      clarification.
                    </li>
                  </Box>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="features"
                >
                  Features and benefits of Electronic Fund Withdrawal
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <Box
                    component="ul"
                    sx={{ pl: 3, mb: 3, '& li': { mb: 2, lineHeight: 1.7 } }}
                  >
                    <li>
                      <b>Simple and Convenient:</b> Both individuals and
                      businesses can e-file and pay their taxes in one easy step
                      using EFW—no extra steps or separate portals required.
                    </li>
                    <li>
                      <b>No IRS Fee:</b> The IRS does not charge any fees for
                      using EFW. However, it&apos;s a good idea to check with
                      your bank to see if there are any transaction fees.
                    </li>
                    <li>
                      <b>No Paper Vouchers Needed:</b> EFW eliminates the need
                      to submit separate payment vouchers—everything is handled
                      electronically during filing.
                    </li>
                    <li>
                      <b>Secure and Confidential:</b> Your payment details are
                      encrypted and used only for the authorized tax payment.
                      EFW ensures your financial information remains protected.
                      Your bank account information is securely protected and
                      used solely for processing the authorized tax payment. It
                      will not be shared or disclosed for any other purpose.
                    </li>
                  </Box>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Payment Scheduling
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <Box
                    component="ul"
                    sx={{ pl: 3, mb: 3, '& li': { mb: 2, lineHeight: 1.7 } }}
                  >
                    <li>
                      Most <b>individual and business taxpayers</b> can schedule
                      EFW payments for a future date—
                      <b>up to the return due date</b>.
                    </li>

                    <li>
                      After the return due date, the payment must be dated{' '}
                      <b>either on the filing date</b> or{' '}
                      <b>within the previous five days</b>.
                    </li>
                    <li>
                      Your e-file software will clearly indicate whether{' '}
                      <b>payment scheduling is available</b> for your return. If
                      an invalid payment date is entered, you’ll be prompted to
                      correct it.
                    </li>
                  </Box>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                  }}
                >
                  Estimated Tax Payments
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <Box
                    component="ul"
                    sx={{ pl: 3, mb: 3, '& li': { mb: 2, lineHeight: 1.7 } }}
                  >
                    <li>
                      For individuals, up to four <b>1040-ES</b> estimated
                      payments for the current year can be submitted with the
                      previous year&apos;s return.
                    </li>
                    <li>
                      Each estimated payment must be submitted with a{' '}
                      <b>separate payment</b> authorization.
                    </li>
                    <li>
                      You can use the <b>same or different bank account</b> for
                      each estimated tax payment, whether for individuals or
                      businesses.
                    </li>
                  </Box>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 88,
                  }}
                  id="info"
                >
                  Important Information About Using EFW
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  1. Do businesses need a separate EFW account?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    No, businesses do not need to create a separate account for
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Electronic Funds Withdrawal (EFW)
                    </span>
                    .
                  </li>
                  <li>
                    Businesses only need to provide valid bank account
                    details—such as the routing number, account number, and
                    payment date—during the e-filing process.{' '}
                  </li>
                  <li>
                    Once the return is accepted, the IRS will automatically
                    withdraw the scheduled payment from the specified bank
                    account.{' '}
                  </li>
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    2. What payment methods does EFW support?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Only <b>direct debit</b> from a{' '}
                    <b>U.S. checking or savings account</b> is supported.
                    Credit/debit cards and international accounts are not
                    accepted.
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    3. Can you automate recurring EFW payments?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    No. EFW supports <b>only one-time</b> payments per e-filed
                    return. For recurring payments, use EFTPS.
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    4. How do I get an EFW payment receipt?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    After submitting your return with EFW selected, you’ll
                    receive confirmation from TaxZerone. This serves as your
                    receipt and includes the payment date, amount, and
                    confirmation number.
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    5. Can state taxes be paid through EFW?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    No. EFW is strictly an IRS payment method for{' '}
                    <b>federal taxes only.</b> State tax payments must be made
                    through your respective state’s tax authority or payment
                    portal.
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    6. Can sole proprietors enroll in EFW as a business?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Yes. Sole proprietors can use EFW to pay business-related
                    taxes as long as they file forms like <b>Schedule C</b> with
                    their Form 1040 and provide valid business banking
                    information.
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    7. Is there a fee to use EFW?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    No. The IRS does <b>not</b> charge any fees for using
                    Electronic Funds Withdrawal and TaxZerone also does not
                    charge additional fees. Your bank may charge an NSF fee if
                    there are insufficient funds, but the payment itself is
                    free.
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    8. Can I verify my payment status at any time?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Yes. You can check your payment status by reviewing your
                    bank statement or by contacting the IRS directly. If you
                    filed using TaxZerone, you will receive an electronic
                    confirmation for your filing.
                  </Typography>

                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: '18px !important',
                      fontWeight: 600,
                      mb: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    9. Are Foreign Taxpayers Eligible to Use EFW?
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1.5 }}>
                    Yes — but{' '}
                    <b>
                      only if the payment is made from a U.S.-based bank
                      account.
                    </b>
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1.5 }}>
                    Foreign taxpayers must have a U.S. checking or savings
                    account to use EFW. If you do not have one, the IRS
                    recommends alternative payment methods such as:
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 3, '& li': { mb: 1.2, listStyleType: 'disc' } }}
                  >
                    <li>
                      <b>International wire transfers</b> via the IRS Foreign
                      Electronic Payments system
                    </li>
                    <li>
                      <b>Credit or debit card payments</b> through
                      IRS-authorized processors
                    </li>
                  </Box>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b>Note:</b> EFW is <b>not supported</b> for international
                    bank accounts or foreign currencies. period.
                  </Alert>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided above is based on official IRS
                        guidelines. For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/payments/pay-taxes-by-electronic-funds-withdrawal"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS Electronic Funds Withdrawal (EFW) page
                        </Link>{' '}
                        <Link
                          href="https://www.irs.gov/payments"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Make a payment
                        </Link>
                        , payment options for{' '}
                        <Link
                          href="https://www.irs.gov/payments/electronic-funds-withdrawal-and-credit-or-debit-card-payment-options-for-businesses"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          businesses
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/payments/electronic-funds-withdrawal-and-credit-or-debit-card-payment-options-for-individuals"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          individuals
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file and pay your federal taxes in an easy way?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              File your return and pay securely with Electronic Funds Withdrawal
              (EFW) through TaxZerone.
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=efw`}
              >
                Start Filing Now
              </Button>
              {/* <Typography variant="body2" sx={{ mt: 1 }}>
                Easy. Fast. Precise
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default EFTPS;
